/**
 * Console session policy:
 * - Absolute login lifetime: 1 hour from sign-in
 * - Idle timeout: 30 minutes without pointer/keyboard/touch activity
 *
 * Keycloak realm SSO timeouts are seeded to match; this module enforces the
 * same rules in the browser (including Demo) and refreshes the access token
 * while the session is still within those windows.
 */

import {
  clearAllAuth,
  getAccessToken,
  isAccessTokenExpired,
  isDemoSession,
  logout,
  refreshAccessToken,
  touchSessionActivity,
  sessionIdleExceeded,
  sessionMaxExceeded,
  markSessionStartedIfNeeded,
} from '@/auth/oidc'
import { flashToast, toastCopy } from '@/ui/toaster'

const CHECK_EVERY_MS = 15_000

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
  'click',
]

let started = false
let activityTimer: number | null = null
let checkTimer: number | null = null

function endForIdle(): void {
  flashToast(toastCopy.sessionIdle)
  logout({ silent: true })
}

function endForMax(): void {
  flashToast(toastCopy.sessionMax)
  logout({ silent: true })
}

async function tick(): Promise<void> {
  if (!getAccessToken() && !isDemoSession()) return

  markSessionStartedIfNeeded()

  if (sessionMaxExceeded()) {
    endForMax()
    return
  }
  if (sessionIdleExceeded()) {
    endForIdle()
    return
  }

  // Keep the access token fresh while the operator is active (within the 1h cap).
  if (!isDemoSession() && isAccessTokenExpired()) {
    const ok = await refreshAccessToken()
    if (!ok) {
      clearAllAuth()
      flashToast(toastCopy.loginExpired)
      location.assign('/login?reason=expired')
    }
  }
}

function onActivity(): void {
  // Coalesce high-frequency mousemove into one touch per second.
  if (activityTimer != null) return
  activityTimer = window.setTimeout(() => {
    activityTimer = null
    touchSessionActivity()
  }, 1_000)
}

/** Start watching once per page load (AuthGate mounts this). */
export function startSessionWatch(): () => void {
  if (started) return () => undefined
  started = true

  markSessionStartedIfNeeded()
  touchSessionActivity()

  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, onActivity, { passive: true, capture: true })
  }

  void tick()
  checkTimer = window.setInterval(() => {
    void tick()
  }, CHECK_EVERY_MS)

  return () => {
    started = false
    if (checkTimer != null) window.clearInterval(checkTimer)
    checkTimer = null
    if (activityTimer != null) window.clearTimeout(activityTimer)
    activityTimer = null
    for (const ev of ACTIVITY_EVENTS) {
      window.removeEventListener(ev, onActivity, true)
    }
  }
}
